import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import StoreIcon from '@mui/icons-material/Store';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyIcon from '@mui/icons-material/Money';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import InfoIcon from '@mui/icons-material/Info';

// Custom MUI theme with light and dark modes
const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: '#3B82F6' },
      secondary: { main: '#EF4444' },
      background: {
        default: mode === 'light' ? '#F9FAFB' : '#111827',
        paper: mode === 'light' ? '#FFFFFF' : '#1F2937',
      },
      text: {
        primary: mode === 'light' ? '#111827' : '#F9FAFB',
        secondary: mode === 'light' ? '#6B7280' : '#9CA3AF',
      },
      chartColors: ['#3B82F6', '#EF4444', '#FBBF24', '#10B981', '#F97316', '#6EE7B7'],
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      h4: { fontWeight: 700, fontSize: '2.25rem' },
      h5: { fontWeight: 600, fontSize: '1.5rem' },
      h6: { fontWeight: 600, fontSize: '1.25rem' },
      body1: { fontWeight: 500, fontSize: '1rem' },
      body2: { fontWeight: 400, fontSize: '0.875rem' },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            background: mode === 'light'
              ? 'linear-gradient(145deg, #FFFFFF 0%, #F9FAFB 100%)'
              : 'linear-gradient(145deg, #1F2937 0%, #374151 100%)',
            boxShadow: mode === 'light'
              ? '0 4px 20px rgba(0, 0, 0, 0.05)'
              : '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: mode === 'light'
                ? '0 8px 30px rgba(0, 0, 0, 0.1)'
                : '0 8px 30px rgba(0, 0, 0, 0.5)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: mode === 'light'
              ? 'linear-gradient(90deg, #FFFFFF 0%, #F3F4F6 100%)'
              : 'linear-gradient(90deg, #1F2937 0%, #374151 100%)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
  });

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
          color: '#111827',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          zIndex: 1000,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2">{`Valeur: ${payload[0].value}`}</Typography>
        {payload[0].payload.percent && (
          <Typography variant="body2">{`Pourcentage: ${(payload[0].payload.percent * 100).toFixed(1)}%`}</Typography>
        )}
      </Box>
    );
  }
  return null;
};

// Styled IconButton for dark mode toggle
const ModeIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  borderRadius: '50%',
  padding: '8px',
  transition: 'background-color 0.3s ease, transform 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'rotate(15deg)',
  },
}));

// Utility function to format numbers with toFixed safely
const formatNumber = (value, decimals = 2) => {
  const num = Number(value);
  if (!isNaN(num)) {
    return num.toFixed(decimals);
  }
  return '0.00';
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('light');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://apibackoffice.lalivraison.ma/api/restaurants/stats', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!response.ok) {
          throw new Error(`Échec de la récupération des statistiques: ${response.statusText}`);
        }
        const data = await response.json();
        console.log('Fetched stats:', data);
        setStats(data);
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
        setError(
          `Impossible de charger les statistiques du tableau de bord: ${error.message}. Veuillez réessayer plus tard.`
        );
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleModeToggle = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = getTheme(mode);
  const COLORS = theme.palette.chartColors;

  // Normalize data for PieCharts to include percent
  const normalizePieData = (data) => {
    if (!data || data.length === 0) return [];
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    return data.map((entry) => ({
      ...entry,
      percent: total > 0 ? entry.value / total : 0,
    }));
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: 'background 0.3s ease',
        }}
      >
        {/* Header */}
        <AppBar position="static">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              }}
            >
              Tableau de Bord
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="body1"
                sx={{
                  bgcolor: 'primary.main',
                  color: '#fff',
                  borderRadius: '20px',
                  px: 2.5,
                  py: 0.75,
                  fontWeight: 500,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                }}
              >
                Données en Temps Réel
              </Typography>
              <Tooltip title={mode === 'light' ? 'Mode Sombre' : 'Mode Clair'}>
                <ModeIconButton onClick={handleModeToggle}>
                  {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                </ModeIconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3, md: 4 },
            maxWidth: '1600px',
            mx: 'auto',
            py: 4,
          }}
        >
          {/* Error Alert */}
          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
                {error}
              </Alert>
            </motion.div>
          )}

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
              <CircularProgress size={60} sx={{ color: 'primary.main' }} />
            </Box>
          ) : stats ? (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {/* Summary Cards */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <StoreIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Total Restaurants
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {stats.totalRestaurants?.toLocaleString() || '0'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Actifs: {stats.statusDistribution?.find((s) => s.name === 'Active')?.value || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ShoppingCartIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Total Commandes
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {stats.orderStats?.totalOrders?.toLocaleString() || '0'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Toutes Commandes
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AttachMoneyIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Valeur Moyenne
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {formatNumber(stats.orderStats?.avgOrderValue)} MAD
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Par Commande
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircleIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Restaurants Actifs
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {stats.statusDistribution?.find((s) => s.name === 'Active')?.value || '0'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Inactifs: {stats.statusDistribution?.find((s) => s.name === 'Inactive')?.value || '0'}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

              {/* Payment and Commission Insights */}
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CreditCardIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Paiements par Carte
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {formatNumber(stats.orderStats?.paymentDistribution?.card)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Commandes par Carte
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                >
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocalAtmIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary' }}>
                          Paiements en Espèces
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {formatNumber(stats.orderStats?.paymentDistribution?.cash)}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Commandes en Espèces
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              {/* Distribution Charts - Centered */}
              <Grid container item xs={12} spacing={{ xs: 2, md: 3 }} justifyContent="center">
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Card sx={{ bgcolor: 'background.paper', p: 2 }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            Commandes par Statut
                          </Typography>
                          <Tooltip title="Répartition des commandes par statut (ex. En cours, Complété).">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {stats?.orderStats?.ordersByStatus && stats.orderStats.ordersByStatus.length > 0 ? (
                          <Box sx={{ width: '365px', height: '350px', marginBottom: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                                <Pie
                                  data={normalizePieData(stats.orderStats.ordersByStatus)}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={90}
                                  dataKey="value"
                                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                  labelLine={true}
                                  label={{ fontSize: 12, position: 'outside', fill: theme.palette.text.primary }}
                                  animationDuration={1000}
                                  onClick={(data) => console.log(`Clicked on status: ${data.name}`)}
                                >
                                  {normalizePieData(stats.orderStats.ordersByStatus).map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                      style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }}
                                    />
                                  ))}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  layout="horizontal"
                                  verticalAlign="bottom"
                                  align="center"
                                  wrapperStyle={{ paddingTop: 20, fontSize: 12 }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            Aucune donnée disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <Card sx={{ bgcolor: 'background.paper', p: 2 }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            Types d'Abonnement
                          </Typography>
                          <Tooltip title="Répartition des restaurants par type d'abonnement (ex. Premium, Standard).">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {stats?.restaurantsBySubscription && stats.restaurantsBySubscription.length > 0 ? (
                          <Box sx={{ width: '365px', height: '350px', marginBottom: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                                <Pie
                                  data={normalizePieData(stats.restaurantsBySubscription)}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={90}
                                  dataKey="value"
                                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                  labelLine={true}
                                  label={{ fontSize: 12, position: 'outside', fill: theme.palette.text.primary }}
                                  animationDuration={1000}
                                  onClick={(data) => console.log(`Clicked on subscription: ${data.name}`)}
                                >
                                  {normalizePieData(stats.restaurantsBySubscription).map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                      style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))' }}
                                    />
                                  ))}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  layout="horizontal"
                                  verticalAlign="bottom"
                                  align="center"
                                  wrapperStyle={{ paddingTop: 20, fontSize: 12 }}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            Aucune donnée disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <Card sx={{ bgcolor: 'background.paper', p: 2 }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            Restaurants par Ville
                          </Typography>
                          <Tooltip title="Nombre de restaurants actifs par ville.">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {stats?.restaurantsByCity && stats.restaurantsByCity.length > 0 ? (
                          <Box sx={{ width: '365px', height: '350px', marginBottom: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={stats.restaurantsByCity}
                                margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke={mode === 'light' ? '#E5E7EB' : '#4B5563'}
                                />
                                <XAxis
                                  dataKey="name"
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={11}
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                  interval={0}
                                  tickMargin={10}
                                />
                                <YAxis
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={12}
                                  tickMargin={10}
                                />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  verticalAlign="top"
                                  height={36}
                                  wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
                                />
                                <Bar
                                  dataKey="count"
                                  fill="url(#barGradient)"
                                  radius={[8, 8, 0, 0]}
                                  animationDuration={1000}
                                  onClick={(data) => console.log(`Clicked on city: ${data.name}`)}
                                />
                                <defs>
                                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={COLORS[0]} />
                                    <stop offset="100%" stopColor={COLORS[1]} />
                                  </linearGradient>
                                </defs>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            Aucune donnée disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    <Card sx={{ bgcolor: 'background.paper', p: 2 }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            Top Restaurants
                          </Typography>
                          <Tooltip title="Classement des restaurants par nombre de commandes.">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {stats?.orderStats?.topRestaurants && stats.orderStats.topRestaurants.length > 0 ? (
                          <Box sx={{ width: '365px', height: '350px', marginBottom: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={stats.orderStats.topRestaurants}
                                margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke={mode === 'light' ? '#E5E7EB' : '#4B5563'}
                                />
                                <XAxis
                                  dataKey="name"
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={11}
                                  angle={-45}
                                  textAnchor="end"
                                  height={80}
                                  interval={0}
                                  tickMargin={10}
                                />
                                <YAxis
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={12}
                                  tickMargin={10}
                                />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  verticalAlign="top"
                                  height={36}
                                  wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
                                />
                                <Bar
                                  dataKey="count"
                                  fill="url(#barGradientTop)"
                                  radius={[8, 8, 0, 0]}
                                  animationDuration={1000}
                                  onClick={(data) => console.log(`Clicked on restaurant: ${data.name}`)}
                                />
                                <defs>
                                  <linearGradient id="barGradientTop" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={COLORS[1]} />
                                    <stop offset="100%" stopColor={COLORS[5]} />
                                  </linearGradient>
                                </defs>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            Aucune donnée disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <Card sx={{ bgcolor: 'background.paper', p: 2 }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            Créations de Restaurants
                          </Typography>
                          <Tooltip title="Évolution du nombre de restaurants créés par période.">
                            <IconButton size="small" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {stats?.restaurantsByCreationDate && stats.restaurantsByCreationDate.length > 0 ? (
                          <Box sx={{ width: '365px', height: '350px', marginBottom: 2 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={stats.restaurantsByCreationDate}
                                margin={{ top: 20, right: 20, bottom: 40, left: 20 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke={mode === 'light' ? '#E5E7EB' : '#4B5563'}
                                />
                                <XAxis
                                  dataKey="name"
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={12}
                                  interval="preserveStartEnd"
                                  tickMargin={10}
                                />
                                <YAxis
                                  stroke={mode === 'light' ? '#6B7280' : '#9CA3AF'}
                                  fontSize={12}
                                  tickMargin={10}
                                />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend
                                  verticalAlign="top"
                                  height={36}
                                  wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="count"
                                  stroke="url(#lineGradient)"
                                  strokeWidth={3}
                                  dot={{ r: 5, fill: COLORS[1] }}
                                  animationDuration={1000}
                                  onClick={(data) => console.log(`Clicked on date: ${data.name}`)}
                                />
                                <defs>
                                  <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={COLORS[1]} />
                                    <stop offset="100%" stopColor={COLORS[5]} />
                                  </linearGradient>
                                </defs>
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 4 }}
                          >
                            Aucune donnée disponible
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 6 }}
            >
              Aucune statistique disponible.
            </Typography>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;